import { FC, useEffect, useMemo } from 'react';
import {
  UseFormRegister,
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import {
  AutofillAssessmentQuestion,
  AutofillSource,
  AssessmentAnswer,
  AssessmentQuestionType,
} from '../../../../../../generated/graphql';
import InputGroup from '../../../../../components/InputGroup';
import CountrySelector from '../../../../../components/CountrySelector';
import LanguageSelector from '../../../../../components/LanguageSelector';
import TimezoneSelector from '../../../../../components/TimezoneSelector';
import SelectMenu from '../../../../../components/SelectMenu';
import QuestionField from './QuestionField';
import {
  getAnswerQuestion,
  getAnswerValue,
} from '../../../../../lib/answerHelpers';
import { getAutofillFieldValidation } from '../../../../../lib/addressValidation';
import { ProgramAssessmentSignatureUserDataFragment } from '../../../../../../generated/graphql';

interface AutofillQuestionProps {
  question: AutofillAssessmentQuestion;
  answer?: AssessmentAnswer;
  idx: number;
  register: UseFormRegister<any>;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  isReadOnly?: boolean; // true for provider forms
  programAssessmentSignatureUsers?: ProgramAssessmentSignatureUserDataFragment[]; // For practitioner selection
  fieldPrefix?: string; // Optional prefix for nested forms (e.g., "forms.assessmentId")
}

const AutofillQuestion: FC<AutofillQuestionProps> = ({
  question,
  answer,
  idx,
  register,
  control,
  setValue,
  isReadOnly = false,
  programAssessmentSignatureUsers = [],
  fieldPrefix = '',
}) => {
  const baseName = `answers.${idx}.value`;
  const fieldName = fieldPrefix ? `${fieldPrefix}.${baseName}` : baseName;
  const isCountryField =
    question.autofillSource === AutofillSource.AddressCountry;
  const isLanguageField =
    question.autofillSource === AutofillSource.PreferredLanguage;
  const isTimezoneField = question.autofillSource === AutofillSource.Timezone;
  const isPractitionerNameField =
    question.autofillSource === AutofillSource.PractitionerName;
  const isPractitionerRoleField =
    question.autofillSource === AutofillSource.PractitionerRole;
  const isParticipantIdField =
    question.autofillSource === AutofillSource.ParticipantId;
  const isEmailField = question.autofillSource === AutofillSource.Email;
  const isDateOfBirthField =
    question.autofillSource === AutofillSource.DateOfBirth;
  const currentValue = useWatch({ control, name: fieldName });

  // Get practitioners from signature users (provider users only)
  const practitioners = useMemo(() => {
    return programAssessmentSignatureUsers
      .filter(
        (user) =>
          user.providerUser && user.providerUser.id && user.providerUser.name,
      )
      .map((user) => ({
        id: user.providerUser!.id!,
        name: user.providerUser!.name!,
        role: user.signatureRole || '',
      }));
  }, [programAssessmentSignatureUsers]);

  // Get default practitioner (first one with a role, or first one)
  const defaultPractitioner = useMemo(() => {
    return (
      practitioners.find((p) => p.role) ||
      practitioners.find((p) => p.id) ||
      practitioners[0]
    );
  }, [practitioners]);

  // Use saved answer if available, otherwise fall back to autofillValue
  // Match answer by questionType and question text
  const existingAnswerValue =
    answer &&
    answer.questionType === AssessmentQuestionType.Autofill &&
    getAnswerQuestion(answer) === question.question
      ? getAnswerValue(answer) ?? ''
      : null;

  // Participant ID and Email are system-generated and always read-only (even for providers).
  // No one can edit them from the form, so saved answers are never meaningful. Always use autofillValue.
  const isSystemGeneratedField = isParticipantIdField || isEmailField;
  const useSavedAnswer =
    existingAnswerValue !== null && !isSystemGeneratedField;

  // Determine default value based on field type
  const autofillValue = question.autofillValue || '';
  let defaultValue = isSystemGeneratedField
    ? autofillValue
    : existingAnswerValue ?? autofillValue;

  // Set smart defaults for practitioner fields when we'd otherwise show empty.
  // Use !existingAnswerValue (not !useSavedAnswer) so empty string '' still triggers defaults.
  if (!existingAnswerValue && !autofillValue) {
    if (isPractitionerNameField && defaultPractitioner) {
      defaultValue = defaultPractitioner.name;
    } else if (isPractitionerRoleField && defaultPractitioner?.role) {
      defaultValue = defaultPractitioner.role;
    }
  }

  // Set form value with priority: saved answer > autofill value > smart defaults > empty
  // Only update if:
  // 1. No current value is set, OR
  // 2. Current value is the autofill value but we have a different saved answer
  useEffect(() => {
    // Skip if form already has a value that matches what we want to set
    if (currentValue === defaultValue) {
      return;
    }

    // If we have a saved answer (and it's not a system-generated field), use it
    if (useSavedAnswer) {
      setValue(fieldName, existingAnswerValue!);
      return;
    }

    // For system-generated fields, always overwrite with autofillValue - draft restoration
    // may have pre-populated currentValue with stale data, and we must show the current value.
    if (isSystemGeneratedField) {
      setValue(fieldName, defaultValue);
      return;
    }

    // Otherwise, use autofill value or smart default if available and no current value
    if (!currentValue && defaultValue) {
      setValue(fieldName, defaultValue);
    }
  }, [
    isSystemGeneratedField,
    useSavedAnswer,
    existingAnswerValue,
    autofillValue,
    currentValue,
    defaultValue,
    setValue,
    fieldName,
  ]);

  // Get validation rules matching Account page behavior
  const validationRules = getAutofillFieldValidation(
    question.autofillSource,
    question.isRequired ?? false,
  );

  if (isCountryField) {
    return (
      <div>
        <QuestionField question={question.question} />
        <Controller
          name={fieldName}
          control={control}
          rules={validationRules}
          render={({ field, fieldState }) => (
            <div className="w-full md:w-[398px]">
              <CountrySelector
                value={field.value || defaultValue || ''}
                onChange={(value) => {
                  // Match Account page behavior: use setValue with shouldDirty
                  // field.onChange is handled automatically by Controller
                  setValue(fieldName, value, { shouldDirty: true });
                }}
                disabled={isReadOnly}
                errorMessage={fieldState.error?.message}
                required={question.isRequired ?? false}
              />
            </div>
          )}
        />
      </div>
    );
  }

  if (isLanguageField) {
    return (
      <div>
        <QuestionField question={question.question} />
        <Controller
          name={fieldName}
          control={control}
          rules={validationRules}
          render={({ field, fieldState }) => (
            <div className="w-full md:w-[398px]">
              <LanguageSelector
                value={field.value || defaultValue || ''}
                onChange={(value) => {
                  setValue(fieldName, value, { shouldDirty: true });
                }}
                disabled={isReadOnly}
              />
              {fieldState.error?.message && (
                <p className="mt-1 text-caption text-red-100">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />
      </div>
    );
  }

  if (isTimezoneField) {
    return (
      <div>
        <QuestionField question={question.question} />
        <Controller
          name={fieldName}
          control={control}
          rules={validationRules}
          render={({ field, fieldState }) => (
            <div className="w-full md:w-[398px]">
              <TimezoneSelector
                value={field.value || defaultValue || ''}
                onChange={(value) => {
                  setValue(fieldName, value, { shouldDirty: true });
                }}
                disabled={isReadOnly}
              />
              {fieldState.error?.message && (
                <p className="mt-1 text-caption text-red-100">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />
      </div>
    );
  }

  if (isPractitionerNameField) {
    const practitionerOptions = practitioners.map((p) => ({
      label: p.name,
      value: p.name,
    }));

    return (
      <div>
        <QuestionField question={question.question} />
        <Controller
          name={fieldName}
          control={control}
          rules={validationRules}
          render={({ field, fieldState }) => (
            <div className="w-full md:w-[398px]">
              <SelectMenu
                fieldValue={
                  field.value || defaultValue || defaultPractitioner?.name || ''
                }
                onChange={(value: string) => {
                  field.onChange(value);
                  setValue(fieldName, value, { shouldDirty: true });
                }}
                fieldOptions={practitionerOptions}
                label={question.question}
                hideLabel
                placeholder="Select practitioner"
                disabled={isReadOnly || practitionerOptions.length === 0}
              />
              {fieldState.error?.message && (
                <p className="mt-1 text-caption text-red-100">
                  {fieldState.error.message}
                </p>
              )}
            </div>
          )}
        />
      </div>
    );
  }

  if (isPractitionerRoleField) {
    // Practitioner Role is editable text input (not a dropdown)
    return (
      <div>
        <QuestionField question={question.question} />
        <InputGroup
          label={question.question}
          {...register(fieldName, validationRules)}
          defaultValue={defaultValue}
          readOnly={isReadOnly}
          labelHidden
          inputSize="extra-small"
          containerClassName="w-full md:w-[398px]"
          className={isReadOnly ? 'cursor-not-allowed bg-neutral-50' : ''}
        />
      </div>
    );
  }

  // Date of Birth should use date input type (matches Account page behavior)
  if (isDateOfBirthField) {
    // Date validation is now included in validationRules via getAutofillFieldValidation
    return (
      <div>
        <QuestionField question={question.question} />
        <InputGroup
          label={question.question}
          type="date"
          {...register(fieldName, validationRules)}
          defaultValue={defaultValue}
          readOnly={isReadOnly}
          labelHidden
          inputSize="extra-small"
          containerClassName="w-full md:w-[398px]"
          className={isReadOnly ? 'cursor-not-allowed bg-neutral-50' : ''}
        />
      </div>
    );
  }

  // Participant ID and Email should always be read-only
  // Email changes can cause authentication issues (email is used for login/auth tokens)
  // Participant ID is system-generated and should not be modified
  const shouldBeReadOnly = isReadOnly || isParticipantIdField || isEmailField;

  return (
    <div>
      <QuestionField question={question.question} />
      <InputGroup
        label={question.question}
        type={isEmailField ? 'email' : undefined}
        {...register(fieldName, validationRules)}
        defaultValue={defaultValue}
        readOnly={shouldBeReadOnly}
        labelHidden
        inputSize="extra-small"
        containerClassName="w-full md:w-[398px]"
        className={shouldBeReadOnly ? 'cursor-not-allowed bg-neutral-50' : ''}
      />
    </div>
  );
};

export default AutofillQuestion;
